import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Package, Users, Undo2, XCircle, Building2 } from "lucide-react";

const TABS = [
    ["pipeline-health", "Pipeline Health", TrendingUp],
    ["product-performance", "Product Performance", Package],
    ["top-customers", "Top Customers", Users],
    ["returns-net-sales", "Returns & Net Sales", Undo2],
    ["lost-reasons", "Lost Reasons", XCircle],
];

const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" });

export default function Reports() {
    const [tab, setTab] = useState("pipeline-health");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setData(null);
        api.get(`/reports/crm/${tab}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) toast.error(formatApiError(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [tab]);

    const scope = data?.scope;

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="reports-page">
            <div>
                <div className="sc-section-title">Reports</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">CRM &amp; Sales Reports</h1>
                <p className="text-sm text-muted-foreground mt-1">Branch Managers see their own branch only; Admin sees all branches.</p>
            </div>

            {scope && (
                <div className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-1.5 text-sm">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{scope.branch}</span>
                    <span className="text-xs text-muted-foreground">{scope.scoped ? "· your branch only" : "· all branches"}</span>
                </div>
            )}

            <div className="flex flex-wrap gap-2 border-b border-border">
                {TABS.map(([key, label, Icon]) => (
                    <button key={key} onClick={() => setTab(key)}
                        className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px ${tab === key ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        data-testid={`report-tab-${key}`}>
                        <Icon className="w-4 h-4" /> {label}
                    </button>
                ))}
            </div>

            {loading && <div className="text-sm text-muted-foreground py-10 text-center">Loading report…</div>}

            {!loading && data && tab === "pipeline-health" && <PipelineHealth d={data} />}
            {!loading && data && tab === "product-performance" && <ProductPerformance d={data} />}
            {!loading && data && tab === "top-customers" && <TopCustomers d={data} />}
            {!loading && data && tab === "returns-net-sales" && <ReturnsNetSales d={data} />}
            {!loading && data && tab === "lost-reasons" && <LostReasons d={data} />}
        </div>
    );
}

function Card({ label, value, icon: Icon }) {
    return (
        <div className="sc-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</div>
            <div className="text-2xl font-semibold mt-1">{value}</div>
        </div>
    );
}

function Table({ head, children, min = 640 }) {
    return (
        <div className="sc-card overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full sc-table" style={{ minWidth: min }}>
                    <thead><tr>{head.map((h, i) => <th key={i} className={i === 0 ? "" : "text-right"}>{h}</th>)}</tr></thead>
                    <tbody>{children}</tbody>
                </table>
            </div>
        </div>
    );
}

function PipelineHealth({ d }) {
    const maxF = Math.max(1, ...(d.funnel || []).map((f) => f.count));
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <Card label="Total Leads" value={num(d.total_leads)} icon={TrendingUp} />
                <Card label="Converted" value={num(d.converted)} />
                <Card label="Conversion Rate" value={`${d.conversion_rate}%`} />
            </div>
            <div className="sc-card p-4">
                <div className="sc-section-title mb-3">Quotation funnel</div>
                <div className="space-y-2">
                    {(d.funnel || []).map((f) => (
                        <div key={f.stage} className="flex items-center gap-3">
                            <div className="w-44 text-xs text-muted-foreground shrink-0">{f.stage}</div>
                            <div className="flex-1 bg-secondary/40 rounded h-6 overflow-hidden">
                                <div className="h-6 bg-foreground/80 rounded" style={{ width: `${(f.count / maxF) * 100}%` }} />
                            </div>
                            <div className="w-16 text-right text-sm font-medium">{num(f.count)}</div>
                        </div>
                    ))}
                </div>
            </div>
            <Table head={["Source", "Leads", "Converted", "Rate"]}>
                {(d.by_source || []).map((r) => (
                    <tr key={r.source}>
                        <td>{r.source}</td>
                        <td className="sc-num">{num(r.total)}</td>
                        <td className="sc-num">{num(r.converted)}</td>
                        <td className="sc-num">{r.rate}%</td>
                    </tr>
                ))}
            </Table>
        </div>
    );
}

function ProductPerformance({ d }) {
    return (
        <div className="space-y-4">
            <Card label="Sales Orders in scope" value={num(d.order_count)} icon={Package} />
            <Table head={["Item", "Qty Ordered", "Amount", "Orders"]} min={640}>
                {(d.items || []).length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No sales-order line data in scope.</td></tr>}
                {(d.items || []).map((r) => (
                    <tr key={r.item_code}>
                        <td><div className="font-mono text-xs">{r.item_code}</div><div className="text-xs text-muted-foreground">{r.item_name}</div></td>
                        <td className="sc-num">{num(r.qty)}</td>
                        <td className="sc-num">{peso(r.amount)}</td>
                        <td className="sc-num">{num(r.orders)}</td>
                    </tr>
                ))}
            </Table>
        </div>
    );
}

function TopCustomers({ d }) {
    return (
        <Table head={["Customer", "Deals", "Deal Value", "Expected Rev."]} min={640}>
            {(d.customers || []).length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No deals in scope.</td></tr>}
            {(d.customers || []).map((r) => (
                <tr key={r.customer}>
                    <td className="font-medium">{r.customer}</td>
                    <td className="sc-num">{num(r.deals)}</td>
                    <td className="sc-num">{peso(r.amount)}</td>
                    <td className="sc-num">{peso(r.expected)}</td>
                </tr>
            ))}
        </Table>
    );
}

function ReturnsNetSales({ d }) {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card label="Orders" value={num(d.order_count)} />
                <Card label="Gross Sales" value={peso(d.gross_sales)} />
                <Card label="Returns" value={peso(d.returns_total)} icon={Undo2} />
                <Card label="Net Sales" value={peso(d.net_sales)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Card label="Closed-Deal Value (CRM)" value={peso(d.closed_deal_value)} />
                <Card label="Closed Deals" value={num(d.closed_deal_count)} />
            </div>
            {d.note && <p className="text-xs text-muted-foreground">{d.note}</p>}
        </div>
    );
}

function LostReasons({ d }) {
    return (
        <div className="space-y-4">
            <Card label="Total Lost Leads" value={num(d.total_lost)} icon={XCircle} />
            <Table head={["Reason (by source)", "Count"]} min={420}>
                {(d.by_reason || []).length === 0 && <tr><td colSpan={2} className="text-center py-8 text-muted-foreground">No lost leads in scope.</td></tr>}
                {(d.by_reason || []).map((r) => (
                    <tr key={r.reason}><td>{r.reason}</td><td className="sc-num">{num(r.count)}</td></tr>
                ))}
            </Table>
            {d.note && <p className="text-xs text-muted-foreground">{d.note}</p>}
        </div>
    );
}
